import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import * as api from "../../services/api";
import LoadingScreen from "../../components/ui/LoadingScreen";
import styles from "./TvDisplayPage.module.css";

const TvDisplayPage: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: event, isLoading: loadingEvent } = useQuery({
    queryKey: ["admin", "event", eventId],
    queryFn: () => api.getEvent(eventId!),
    enabled: !!eventId,
    refetchInterval: 8000,
  });

  const { data: allQueue = [] } = useQuery({
    queryKey: ["admin", "queue", event?.organizationId || event?.artistId],
    queryFn: () => api.getQueue((event!.organizationId || event!.artistId)!),
    enabled: !!(event?.organizationId || event?.artistId),
    refetchInterval: 8000,
  });

  const queue = allQueue.filter((c: { eventId?: string }) => c.eventId === eventId);

  const waiting = queue.filter((c: { status: string }) => c.status === "WAITING");
  const inProgress = queue.filter((c: { status: string }) => c.status === "IN_PROGRESS");
  const completed = useMemo(
    () =>
      queue
        .filter((c: { status: string }) => c.status === "COMPLETED")
        .sort((a: { completedAt?: string }, b: { completedAt?: string }) =>
          (b.completedAt || "").localeCompare(a.completedAt || ""),
        )
        .slice(0, 5),
    [queue],
  );

  const current = inProgress[0] as { position?: number | null; name?: string } | undefined;
  const avg = event?.avgTimePerClient ?? 0;

  if (loadingEvent || !event) {
    return <LoadingScreen message={t("common.loading")} />;
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.tv}>
        <section className={styles.board}>
          <svg
            className={styles.doodle}
            width="90"
            height="90"
            viewBox="0 0 90 90"
            fill="none"
            aria-hidden="true"
          >
            <path d="M20 40h44v20a16 16 0 0 1-16 16H36a16 16 0 0 1-16-16z" stroke="#F4C95D" strokeWidth="3" />
            <path d="M64 46h6a8 8 0 0 1 0 16h-6" stroke="#F4C95D" strokeWidth="3" />
            <g>
              <path d="M32 30c-4-6 4-8 0-14" stroke="#EADBC3" strokeWidth="3" strokeLinecap="round" />
              <path d="M42 30c-4-6 4-8 0-14" stroke="#EADBC3" strokeWidth="3" strokeLinecap="round" />
              <path d="M52 30c-4-6 4-8 0-14" stroke="#EADBC3" strokeWidth="3" strokeLinecap="round" />
            </g>
          </svg>
          <span className={styles.hand}>
            {event.name}
            {event.location ? ` · ${event.location}` : ""}
          </span>
          <div>
            <h2>{t("look.nowWeServe")}</h2>
            <span className={styles.bignum}>
              <small>n.º</small>
              {current?.position ?? "—"}
            </span>
          </div>
          <div className={styles.tvWait}>
            <div>
              <b>{waiting.length}</b>
              <span>{t("look.waitingPeople")}</span>
            </div>
            <div>
              <b>{avg ? `~${avg} min` : "—"}</b>
              <span>{t("look.perPerson")}</span>
            </div>
          </div>
        </section>

        <aside className={styles.sideCol}>
          <div className={styles.side}>
            <div className={styles.listHead}>
              <h2>{t("look.lastCalled")}</h2>
            </div>
            <ul className={styles.called}>
              {completed.length === 0 ? (
                <li>—</li>
              ) : (
                completed.map((c: { id: string; position?: number | null; name: string }, i: number) => (
                  <li key={c.id}>
                    n.º {c.position ?? c.name}
                    <span>{i === 0 ? t("look.justNow") : t("look.minutesAgo", { n: i * 4 })}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
          <div className={styles.tvQr}>
            {event.qrCodeUrl ? (
              <div className={styles.qr}>
                <img src={event.qrCodeUrl} alt="" />
              </div>
            ) : null}
            <b>{t("look.noNumberYet")}</b>
            <span>{t("look.scanPhone", { code: event.code })}</span>
          </div>
          <button type="button" className={styles.exit} onClick={() => navigate(`/admin/events/${eventId}`)}>
            {t("common.back")}
          </button>
        </aside>
      </div>
    </div>
  );
};

export default TvDisplayPage;
